import React, { PureComponent, Fragment } from 'react';

import Helmet from 'react-helmet';
import { FormattedMessage, injectIntl, InjectedIntl } from 'react-intl';
import { connect } from 'react-redux';

import { Grid, GridColumn } from '@atlaskit/page';
import {
  ContextualAnalyticsData,
  FireScreenAnalytics,
  SCREEN,
} from '@atlassian/analytics-bridge';

import { FocusedPageHeader } from 'src/components/accessibility';
import { getMainBranch } from 'src/redux/branches';
import CreateBranchDialog from 'src/sections/create-branch/containers/dialog';
import RepositoryAccessGuard from 'src/sections/repository/components/repository-access-guard';
import Breadcrumbs from 'src/sections/repository/containers/breadcrumbs';
import { REPO_EVENT_SOURCES } from 'src/sections/repository/types';
import { getUpToSmallBreakpointState } from 'src/selectors/global-selectors';
import { BucketState } from 'src/types/state';
import { publishUiEvent, FilterEventMap } from 'src/utils/analytics/publish';

import * as styles from '../../../styles/page';
import BranchMergeDialog from '../../branch/containers/branch-merge-dialog';
import BranchList from '../containers/branch-list';
import BranchListFilters from '../containers/branch-list-filters';
import BranchesPagination from '../containers/branches-pagination';
import DeleteBranchDialog from '../containers/delete-branch-dialog';
import { Sort, Filter, DetailedBranch } from '../types';

import { BranchListActions } from './branch-list-actions';
import messages from './branches-page.i18n';
import { DeleteSelectedBranchesActions } from './bulk-delete-branches-actions';
import { BulkDeleteBranchesDialog } from './dialogs/bulk-delete-branches-dialog';
import EmptyState from './empty-state';

type Props = {
  intl: InjectedIntl;
  isEmpty: boolean;
  isStale: boolean;
  sort: Sort;
  page: number;
  filter: Filter;
  repositoryFullSlug: string;
  getPaginationUrl: (page: number) => string;
  onLoadPage: () => void;
  onSortChange: (sort: Sort) => void;
  onPageChange: () => void;
  onFilterChange: (filter: Partial<Filter>) => void;
  isUpToSmallBreakpointActive: boolean;
  mainBranch: DetailedBranch;
};

const UI_EVENT_TEMPLATE = {
  action: 'changed',
  actionSubject: 'option',
  source: REPO_EVENT_SOURCES.BRANCHES_SCREEN,
};

export class BranchListPage extends PureComponent<Props> {
  mapFilterToEventCreator: FilterEventMap<Filter> = {
    status: branchStatus => ({
      ...UI_EVENT_TEMPLATE,
      actionSubjectId: 'branchStatusSelect',
      attributes: {
        branchStatus,
      },
    }),
    search: () => ({
      ...UI_EVENT_TEMPLATE,
      actionSubject: 'input',
      actionSubjectId: 'branchSearchInput',
    }),
    branchtype: branchTypes => ({
      ...UI_EVENT_TEMPLATE,
      actionSubjectId: 'branchTypeSelect',
      attributes: {
        branchTypes,
      },
    }),
  };

  handleSortChange = (sort: Sort) => {
    this.props.onSortChange(sort);
  };

  handlePageChange = () => {
    this.props.onPageChange();
  };

  handleFilterChange = (filter: Partial<Filter>) => {
    Object.entries(filter).forEach(([filterKey, filterValue]) => {
      // @ts-ignore TODO: fix noImplicitAny error here
      const createEvent = this.mapFilterToEventCreator[filterKey];
      if (createEvent) {
        publishUiEvent(createEvent(filterValue));
      }
    });

    this.props.onFilterChange(filter);
  };

  renderEmptyState = () => (
    <Fragment>
      <FocusedPageHeader breadcrumbs={<Breadcrumbs />}>
        <FormattedMessage {...messages.pageTitle} />
      </FocusedPageHeader>
      <EmptyState />
    </Fragment>
  );

  renderBranchList = () => {
    const {
      sort,
      page,
      filter,
      onLoadPage,
      getPaginationUrl,
      repositoryFullSlug,
    } = this.props;

    return (
      <Fragment>
        <FocusedPageHeader
          breadcrumbs={<Breadcrumbs />}
          actions={
            <styles.BranchActionsWrapper>
              <CreateBranchDialog
                requiredLevel="write"
                shouldRenderCreateBranchButton
                source="branchesScreen"
              />
              <BranchListActions repositoryFullSlug={repositoryFullSlug} />
            </styles.BranchActionsWrapper>
          }
        >
          <FormattedMessage {...messages.pageTitle} />
        </FocusedPageHeader>
        <styles.FilterContainer>
          <BranchListFilters
            filter={filter}
            onFilterChange={this.handleFilterChange}
          />
          <DeleteSelectedBranchesActions />
        </styles.FilterContainer>
        <BranchList
          sort={sort}
          onLoadPage={onLoadPage}
          onSortChange={this.handleSortChange}
          isStale
          renderPagination={() => (
            <styles.BranchPaginationWrapper>
              <BranchesPagination
                page={page}
                onPageChange={this.handlePageChange}
                getPaginationUrl={getPaginationUrl}
              />
            </styles.BranchPaginationWrapper>
          )}
        />
        <RepositoryAccessGuard requiredLevel="write">
          <DeleteBranchDialog />
          <BranchMergeDialog destinationBranch={this.props.mainBranch} />
          <BulkDeleteBranchesDialog />
        </RepositoryAccessGuard>
      </Fragment>
    );
  };

  render() {
    const { intl, isUpToSmallBreakpointActive } = this.props;
    const title = `${intl.formatMessage(messages.pageTitle)}`;

    return (
      <ContextualAnalyticsData sourceName="branches" sourceType={SCREEN}>
        <FireScreenAnalytics />
        <Grid
          layout="fluid"
          spacing={isUpToSmallBreakpointActive ? 'cosy' : 'comfortable'}
        >
          <Helmet defer={false} title={title} />
          <GridColumn>
            {this.props.isEmpty
              ? this.renderEmptyState()
              : this.renderBranchList()}
          </GridColumn>
        </Grid>
      </ContextualAnalyticsData>
    );
  }
}

const mapStateToProps = (state: BucketState) => ({
  isUpToSmallBreakpointActive: getUpToSmallBreakpointState(state),
  mainBranch: getMainBranch(state),
});

export default connect(mapStateToProps)(injectIntl(BranchListPage));
